import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    // Notify that server is back up if it was down
    if (window.__appStatusManager?.markServerUp) {
      window.__appStatusManager.markServerUp();
    }

    return response;
  },
  (error) => {
    const errorData = error.response?.data;
    console.error('🔴 API ERROR:', {
      url: error.config?.url,
      status: error.response?.status,
      data: errorData,
      message: errorData?.message || errorData?.error || 'Unknown error',
      fullError: JSON.stringify(errorData, null, 2)
    });

    // Detect different types of errors and notify
    if (!error.response) {
      // Network error or server unreachable
      if (error.code === 'ECONNABORTED') {
        if (window.__appStatusManager?.markServerDown) {
          window.__appStatusManager.markServerDown('network',
            'Network timeout. The server may be unreachable.');
        }
      } else if (error.message === 'Network Error') {
        if (window.__appStatusManager?.markServerDown) {
          window.__appStatusManager.markServerDown('network',
            'Network connection lost. Please check your internet.');
        }
      } else {
        if (window.__appStatusManager?.markServerDown) {
          window.__appStatusManager.markServerDown('server',
            'Unable to reach the server. We\'re working to restore service.');
        }
      }
    } else if (error.response.status >= 500) {
      // Server error (5xx)
      if (window.__appStatusManager?.markServerDown) {
        window.__appStatusManager.markServerDown('server',
          'Server error. Our team is investigating.');
      }
    } else if (error.response.status === 503) {
      // Service unavailable
      if (window.__appStatusManager?.markServerDown) {
        window.__appStatusManager.markServerDown('server',
          'Service temporarily unavailable. Maintenance in progress.');
      }
    }

    return Promise.reject(error);
  }
);

// Headcoach-gated coach sign-up: creating a coach account requires an
// invite link from a headcoach (see routes/coachInvites.js) - there's no
// public coach registration.
export const coachInvites = {
  create: (email) => api.post('/coach-invites', email ? { email } : {}),
  list: () => api.get('/coach-invites'),
  revoke: (inviteId) => api.delete(`/coach-invites/${inviteId}`),
  // Public - no auth token needed, used by the invite-only sign-up page
  validate: (token) => api.get(`/coach-invites/validate/${token}`),
  accept: (token, data) => api.post(`/coach-invites/accept/${token}`, data),
};

export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),

  // ✅ PASSWORD RESET ENDPOINTS
  requestPasswordReset: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),

  // ✅ PASSWORD CHANGE ENDPOINT (authenticated users)
  changePassword: (data) => api.post('/auth/change-password', data),

  // Delete the signed-in player's own account and data (password + typed DELETE required)
  deleteAccount: (password, confirm) => api.delete('/auth/account', { data: { password, confirm } }),

  // ✅ FCM TOKEN REGISTRATION (for push notifications)
  registerFcmToken: (fcmToken) => api.post('/auth/register-fcm-token', { fcmToken }),

  // Notification preferences (e.g. coach "lesson starting soon" briefing)
  getPreferences: () => api.get('/auth/preferences'),
  sendTestNotification: () => api.post('/auth/test-notification'),
  updatePreferences: (prefs) => api.patch('/auth/preferences', prefs)
};

export const notes = {
  getMyNotes: () => api.get('/notes/my-notes'),
  getAllPlayers: () => api.get('/notes/players'),
  searchPlayers: (query) => api.get('/notes/search-players', { params: { query } }),
  
    // ✅ ANALYTICS
  getCoachAnalytics: () => api.get('/notes/coach-analytics'),
  
  // ✅ TRACKING
  trackNoteView: (noteId) => api.post(`/notes/track-view/${noteId}`),
  trackNoteRead: (noteId) => api.post(`/notes/track-read/${noteId}`),
  getNoteStats: (playerId, noteId) => api.get(`/notes/note-stats/${playerId}/${noteId}`),
  
  addNote: (playerId, text, performanceBadge = null, noteType = null) => {
    console.log('  text:', text.substring(0, 30));
    
    const payload = { 
      text, 
      performanceBadge,
      noteType
    };
    
    console.log('📦 Payload being sent:', JSON.stringify(payload));
    
    return api.post(`/notes/players/${playerId}/notes`, payload);
  },
  
  deleteNote: (playerId, noteId) => 
    api.delete(`/notes/players/${playerId}/notes/${noteId}`),
  
  updateNote: (playerId, noteId, text, performanceBadge = null, noteType = null) => 
    api.put(`/notes/players/${playerId}/notes/${noteId}`, { text, performanceBadge, noteType }),
  
  saveFcmToken: (fcmToken) => api.post('/notes/save-token', { fcmToken }),
  addStudent: (email) => api.post('/notes/students', { email }),
  removeStudent: (studentId) => api.delete(`/notes/students/${studentId}`),
  updateStudentRole: (studentId, newRole) => api.put(`/notes/students/${studentId}/role`, { newRole }),
  getStudents: () => api.get('/notes/my-students'),

  getLessonNotes: () => api.get('/notes/lesson-notes'),
  saveLessonNote: (lessonId, lessonTitle, note, lessonEndTime, performanceBadge, noteType) => 
    api.post('/notes/lesson-notes', { 
      lessonId, 
      lessonTitle, 
      note, 
      lessonEndTime,
      performanceBadge,  // ← Add this
      noteType           // ← Add this
    }),

  deleteLessonNote: (lessonId) => api.delete(`/notes/lesson-notes/${lessonId}`),

  // Check if player reached a milestone (every 10 notes)
  checkMilestone: () => api.post('/notes/check-milestone'),

  // ✅ PHOTO ATTACHMENTS
  uploadPhoto: (noteId, file, caption = null, playerId = null) => {
    const formData = new FormData();
    formData.append('photo', file);
    if (caption) {
      formData.append('caption', caption);
    }
    if (playerId) {
      formData.append('playerId', playerId);
    }
    return api.post(`/notes/${noteId}/upload-photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  deleteAttachment: (noteId, attachmentId) =>
    api.delete(`/notes/${noteId}/attachments/${attachmentId}`),

  updateAttachmentCaption: (noteId, attachmentId, caption) =>
    api.patch(`/notes/${noteId}/attachments/${attachmentId}`, { caption })
};

// ← ADD THIS SECTION
export const savedNotes = {
  // Get all saved notes for coach
  getAll: () => api.get('/saved-notes'),

  // Create a new saved note
  create: (data) => api.post('/saved-notes', data),

  // Share saved note to student(s)
  shareToStudent: (noteId, studentIds) =>
    api.post(`/saved-notes/${noteId}/share`, { studentIds }),

  // Delete saved note
  delete: (noteId) => api.delete(`/saved-notes/${noteId}`)
};

// Skill ratings (0-7 coaching feedback)
export const skillRatings = {
  get: (playerId) => api.get(`/notes/skill-ratings/${playerId}`),
  set: (playerId, skillId, rating) =>
    api.post(`/notes/skill-ratings/${playerId}/${skillId}`, { rating }),
};

// Player achievements (persistent, backend-stored)
export const playerAchievements = {
  get:  (playerId)              => api.get(`/notes/achievements/${playerId}`),
  save: (playerId, achievementIds) =>
    api.post(`/notes/achievements/${playerId}`, { achievementIds }),
};

// Google Calendar API (handled by backend)
// Playtomic CSV import: bookings from an export are added to a coach's calendar
export const calendarImport = {
  getConfig: () => api.get('/calendar-import/config'),
  preview: (payload) => api.post('/calendar-import/preview', payload),
  commit: (payload) => api.post('/calendar-import/commit', payload, { timeout: 180000 }),
  getMine: () => api.get('/calendar-import/mine'),
  getHistory: () => api.get('/calendar-import/history'),
  undo: (batchId) => api.post(`/calendar-import/undo/${batchId}`, {}, { timeout: 120000 })
};

// Headcoach: the whole team's coaching hours, venues, repeat customers, distribution and days with no lessons
export const coachingAnalytics = {
  get: (unit, refresh = false) => api.get('/coaching-analytics', { params: { unit, ...(refresh ? { refresh: 1 } : {}) } }),
  getSummary: (refresh = false) => api.get('/coaching-analytics/summary', { params: refresh ? { refresh: 1 } : {} })
};

export const calendar = {
  // Get events for a specific date
  getEventsForDate: (date) => {
    // Format date in LOCAL timezone (not UTC to avoid day shift)
    // toISOString() converts to UTC which shifts date by 1 day in UTC+8 timezones
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    // Include timezone offset so backend can query the correct date
    const timezoneOffset = new Date().getTimezoneOffset(); // Minutes offset from UTC

    return api.get(`/calendar/events`, {
      params: {
        date: dateStr,
        timezoneOffset: timezoneOffset,
        _t: Date.now() // Cache busting - force fresh data from server
      }
    });
  },

  // Update an event
  updateEvent: (eventId, eventData) => {
    return api.patch(`/calendar/events/${eventId}`, eventData);
  },

  // Delete an event
  deleteEvent: (eventId) => {
    return api.delete(`/calendar/events/${eventId}`);
  },

  // Whether this coach has connected their own Google Calendar
  getGoogleStatus: () => api.get('/calendar/google/status'),

  // Get the Google consent URL to redirect the browser to
  getGoogleConnectUrl: () => api.get('/calendar/google/connect'),

  // Unlink this coach's Google Calendar (falls back to the shared calendar)
  disconnectGoogle: () => api.post('/calendar/google/disconnect'),

  // iPhone (native) calendar: the app pushes a rolling window of the phone's events; managers/headcoach read them back
  getDeviceStatus: () => api.get('/calendar/device/status'),
  syncDeviceEvents: (payload) => api.post('/calendar/device/sync', payload),
  disconnectDevice: () => api.post('/calendar/device/disconnect'),
  getDeviceCoaches: () => api.get('/calendar/device/coaches'),
  getDeviceCoachEvents: (coachId, dateStr) => api.get('/calendar/device/events', { params: { coachId, date: dateStr } }),
};

// Private chat: the coach with a student, or a student with their coach / another student (replaces asking the coach a question)
export const chat = {
  getContacts: () => api.get('/chat/contacts'),
  getWith: (userId) => api.get(`/chat/with/${userId}`),
  send: (userId, text) => api.post(`/chat/with/${userId}`, { text }),
  remove: (id) => api.delete(`/chat/messages/${id}`),
  getUnreadCount: () => api.get('/chat/unread-count')
};

// Headcoach task lists: the coach answers each task with yes / no / question (one change allowed)
export const taskLists = {
  getCoaches: () => api.get('/task-lists/coaches'),
  getSent: (coachId) => api.get('/task-lists/sent', { params: { coachId } }),
  send: (payload) => api.post('/task-lists', payload),
  getInbox: () => api.get('/task-lists/inbox'),
  getUnansweredCount: () => api.get('/task-lists/unanswered-count'),
  respond: (listId, itemId, response) => api.post(`/task-lists/${listId}/items/${itemId}/respond`, { response })
};

// Manager API
export const managers = {
  // Get the coach this manager is connected to
  getMyCoach: () =>
    api.get('/managers/my-coach'),

  // Get the coach's schedule for a specific date
  getCoachSchedule: (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    // Include timezone offset so backend can query the correct date
    const timezoneOffset = new Date().getTimezoneOffset(); // Minutes offset from UTC

    return api.get('/managers/coach-schedule', {
      params: {
        date: dateStr,
        timezoneOffset: timezoneOffset
      }
    });
  },

  // Get all students in the coach's roster
  getCoachStudents: () =>
    api.get('/managers/coach-students')
};

// Q&A System - Player asking questions, coaches replying
export const questions = {
  // Player submits a question to their coach
  askQuestion: (coachId, text, skillId = null, lessonId = null, urgencyLevel = 'routine') => {
    const payload = {
      text,
      skillId,
      lessonId,
      urgencyLevel
    };
    // Only include coachId if provided (backend will determine from auth if not provided)
    if (coachId) {
      payload.coachId = coachId;
    }
    return api.post('/questions/ask', payload);
  },

  // Coach gets list of unanswered questions from all players
  getIncomingQuestions: () => {
    return api.get('/questions/incoming');
  },

  // Player gets their question history (asked and answered)
  getMyQuestions: () => {
    return api.get('/questions/my-questions');
  },

  // Coach posts a reply to a question
  answerQuestion: (questionId, answerText) => {
    return api.post(`/questions/${questionId}/answer`, {
      answerText
    });
  },

  // Update question status (pending → answered → resolved)
  updateQuestionStatus: (questionId, status) => {
    return api.patch(`/questions/${questionId}/status`, {
      status
    });
  },

  // Delete a question (player or coach can delete)
  deleteQuestion: (questionId) => {
    return api.delete(`/questions/${questionId}`);
  }
};

// Player Updates System - Unified feed for questions, match shares, thoughts
export const playerUpdates = {
  // Player asks a question
  askQuestion: (data) => {
    return api.post('/player-updates/ask-question', data);
  },

  // Player shares a Playtomic match
  shareMatch: (data) => {
    return api.post('/player-updates/share-match', data);
  },

  // Player posts a thought
  postThought: (data) => {
    return api.post('/player-updates/post-thought', data);
  },

  // Get player's own updates
  getMyUpdates: (params = {}) => {
    return api.get('/player-updates/my-updates', { params });
  },

  // Coach gets all updates from specific player
  getPlayerUpdates: (playerId, filterType = null) => {
    const params = filterType ? { filterType } : {};
    return api.get(`/player-updates/coach/player-updates/${playerId}`, { params });
  },

  // Coach: pending-update count per player, for the overview list's
  // "has a new update" indicator
  getPendingByPlayer: () => {
    return api.get('/player-updates/pending-by-player');
  },

  // Coach replies to an update
  replyToUpdate: (updateId, playerId, replyText) => {
    return api.patch(`/player-updates/${updateId}/reply`, {
      playerId,
      replyText
    });
  },

  // Coach changes update status
  updateStatus: (updateId, playerId, status) => {
    return api.patch(`/player-updates/${updateId}/status`, {
      playerId,
      status
    });
  },

  // Delete an update
  deleteUpdate: (updateId) => {
    return api.delete(`/player-updates/${updateId}`);
  }
};

// Booking System - Coaches offer slots, players request them
export const bookings = {
  // ── AVAILABILITY SLOTS (Coach operations) ──

  // Coach creates new availability slot
  createSlot: (slotData) => {
    return api.post('/bookings/availability', slotData);
  },

  // Coach gets their own availability slots
  getMySlots: () => {
    return api.get('/bookings/availability');
  },

  // Coach updates an existing slot
  updateSlot: (slotId, slotData) => {
    return api.patch(`/bookings/availability/${slotId}`, slotData);
  },

  // Coach archives/deletes a slot
  deleteSlot: (slotId) => {
    return api.delete(`/bookings/availability/${slotId}`);
  },

  // ── CALENDAR BUFFER SYNC (Coach triggered) ──

  // Coach manually triggers buffer slot detection
  syncBufferSlots: () => {
    return api.post('/bookings/sync-buffer-slots', {});
  },

  // Coach cleans up orphaned buffer slots (slots with no calendar event)
  cleanupOrphanedSlots: () => {
    return api.post('/bookings/cleanup-orphaned-slots', {});
  },

  // Debug: get report of orphaned buffer slots
  diagnoseOrphanedSlots: () => {
    return api.get('/bookings/diagnose-orphaned-slots');
  },

  // ── BOOKING REQUESTS (Anyone can request) ──

  // Request a coaching slot
  requestSlot: (slotId, message = null, partySize = undefined, participants = undefined) => {
    return api.post('/bookings/request', { slotId, message, partySize, participants });
  },

  // In-app banner: this week's featured / last-minute open slots from the student's coach(es)
  getMyCoachHighlights: () => api.get('/bookings/my-coach-highlights'),

  // Coach: star / un-star a slot for the students' banner (optional short message)
  featureSlot: (slotId, featured, message = '') =>
    api.patch(`/bookings/availability/${slotId}/feature`, { featured, message }),

  // Coach: how many students each push audience would reach for these slots
  getAdvertiseAudiences: (slotIds) =>
    api.get('/bookings/advertise/audiences', { params: { slotIds: slotIds.join(',') } }),

  // Coach: push open slots to students { slotIds, audience, message, playerIds, feature }
  advertiseSlots: (payload) => api.post('/bookings/advertise', payload),

  // Find registered players by name (id + name only) for the "who is joining" field
  searchParticipants: (query) => {
    return api.get('/bookings/participant-search', { params: { query } });
  },

  // Get user's own booking requests
  getMyRequests: () => {
    return api.get('/bookings/my-requests');
  },

  // Coach gets incoming booking requests
  getIncomingBookings: () => {
    return api.get('/bookings/incoming');
  },

  // Get single booking details
  getBooking: (bookingId) => {
    return api.get(`/bookings/${bookingId}`);
  },

  // Coach approves a booking request
  approveBooking: (bookingId, responseMessage = null) => {
    return api.patch(`/bookings/${bookingId}/approve`, { responseMessage });
  },

  // Coach denies a booking request
  denyBooking: (bookingId, responseMessage = null, slotAction = 'available') => {
    return api.patch(`/bookings/${bookingId}/deny`, { responseMessage, slotAction });
  },

  // Either party cancels a booking
  cancelBooking: (bookingId, reason = null) => {
    return api.patch(`/bookings/${bookingId}/cancel`, { reason });
  },

  // Coach removes a player from an approved booking
  removePlayerFromSlot: (bookingId) => {
    return api.delete(`/bookings/${bookingId}`);
  },

  // ── PUBLIC BROWSING ──

  // ✅ FEATURE 18: Get available session types
  getSessionTypes: () => {
    return api.get('/bookings/session-types');
  },

  // Browse all available slots (public)
  browseSlots: (page = 1, limit = 20, coachId = null, startDate = null, endDate = null, sessionTypes = null) => {
    const params = { page, limit };
    if (coachId) params.coachId = coachId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    // ✅ FEATURE 18: Add sessionTypes filter parameter
    if (sessionTypes && sessionTypes.length > 0) {
      params.sessionTypes = sessionTypes;
    }
    return api.get('/bookings/browse/all', { params });
  },

  // Get specific coach's available slots
  getCoachSlots: (coachId) => {
    return api.get(`/bookings/coach/${coachId}`);
  },

  // ── RECURRING PATTERNS (Feature 3) ──

  // Coach creates a recurring availability pattern
  createRecurringPattern: (patternData) => {
    return api.post('/bookings/recurring', patternData);
  },

  // Coach gets all their recurring patterns
  getRecurringPatterns: () => {
    return api.get('/bookings/recurring');
  },

  // Coach updates a recurring pattern
  updateRecurringPattern: (patternId, patternData) => {
    return api.patch(`/bookings/recurring/${patternId}`, patternData);
  },

  // Coach archives/deletes a recurring pattern
  deleteRecurringPattern: (patternId) => {
    return api.delete(`/bookings/recurring/${patternId}`);
  },

  // Coach pauses/resumes a recurring pattern
  pauseRecurringPattern: (patternId) => {
    return api.post(`/bookings/recurring/${patternId}/pause`, {});
  },

  // ── COACHING PLANS (Feature 4) ──

  // Coach creates a new coaching plan
  createCoachingPlan: (planData) => {
    return api.post('/bookings/plans', planData);
  },

  // Coach gets all their coaching plans
  getCoachingPlans: () => {
    return api.get('/bookings/plans');
  },

  // Browse available coaching plans (public)
  browseCoachingPlans: (page = 1, limit = 20, sessionType = null) => {
    const params = { page, limit };
    if (sessionType) params.sessionType = sessionType;
    return api.get('/bookings/plans/browse/all', { params });
  },

  // Get single coaching plan details
  getCoachingPlan: (planId) => {
    return api.get(`/bookings/plans/${planId}`);
  },

  // Coach updates a coaching plan
  updateCoachingPlan: (planId, planData) => {
    return api.patch(`/bookings/plans/${planId}`, planData);
  },

  // Coach archives a coaching plan
  deleteCoachingPlan: (planId) => {
    return api.delete(`/bookings/plans/${planId}`);
  },

  // Player books an entire coaching plan
  bookCoachingPlan: (planId) => {
    return api.post(`/bookings/plans/${planId}/book`, {});
  },

  // Coach assigns their own available plan directly to one of their students
  assignCoachingPlan: (planId, playerId) => {
    return api.post(`/bookings/plans/${planId}/assign`, { playerId });
  },

  // Player's own booked plans (active + completed), each with `progress`
  getMyBookedPlans: () => {
    return api.get('/bookings/plans/my/booked');
  },

  // Coach marks one plan session's outcome, or undoes it back to 'scheduled'.
  // status: 'completed' | 'no-show' | 'canceled' | 'scheduled'
  markPlanSession: (planId, sequenceNumber, status) => {
    return api.patch(`/bookings/plans/${planId}/sessions/${sequenceNumber}`, { status });
  },

  // Set or move one session's date/time (coach or the booked player).
  // opts: { startTime (ISO), durationMinutes?, availabilitySlotId? }
  schedulePlanSession: (planId, sequenceNumber, opts) => {
    return api.patch(`/bookings/plans/${planId}/sessions/${sequenceNumber}/schedule`, opts);
  },

  // ── WAITLIST (Feature 11) ──

  // Join waitlist for a full slot
  joinWaitlist: (slotId, notes = null) => {
    return api.post(`/bookings/${slotId}/waitlist`, { notes });
  },

  // Get waitlist for a slot (coach-only)
  getWaitlist: (slotId) => {
    return api.get(`/bookings/${slotId}/waitlist`);
  },

  // Get player's waitlist entries
  getMyWaitlistEntries: () => {
    return api.get('/bookings/my/waitlist-entries');
  },

  // Accept waitlist offer
  acceptWaitlistOffer: (waitlistId) => {
    return api.post(`/bookings/waitlist/${waitlistId}/accept`, {});
  },

  // Decline waitlist offer
  declineWaitlistOffer: (waitlistId) => {
    return api.post(`/bookings/waitlist/${waitlistId}/decline`, {});
  },

  // Leave waitlist
  leaveWaitlist: (waitlistId) => {
    return api.delete(`/bookings/waitlist/${waitlistId}`);
  },

  // ── RESCHEDULE (Feature 16) ──

  // Get reschedule suggestions for a canceled booking
  getRescheduleSuggestions: (bookingId) => {
    return api.get(`/bookings/${bookingId}/reschedule-suggestions`);
  },

  // Accept a reschedule suggestion
  acceptReschedule: (bookingId, slotId) => {
    return api.post(`/bookings/${bookingId}/reschedule/${slotId}`, {});
  },

  // Decline all reschedule suggestions
  declineReschedule: (bookingId) => {
    return api.patch(`/bookings/${bookingId}/reschedule/decline`, {});
  },

  // ── DORMANT PLAYER OUTREACH (Feature 1) ──

  // Coach gets list of dormant players
  getDormantPlayers: (threshold = 60) => {
    console.log('😴 Fetching dormant players (threshold:', threshold, 'days)');
    return api.get('/bookings/dormant-players', { params: { threshold } });
  },

  // Send ping to single dormant player
  pingDormantPlayer: (playerId, message) => {
    return api.post(`/bookings/dormant-players/${playerId}/ping`, { message });
  },

  // Send bulk pings to multiple dormant players
  bulkPingDormantPlayers: (playerIds, message) => {
    return api.post('/bookings/dormant-players/bulk-ping', { playerIds, message });
  },

  // Get ping history
  getDormancyHistory: (limit = 50) => {
    return api.get('/bookings/dormant-players/history', { params: { limit } });
  },

  // ── LESSON TYPE WAITLIST (Feature 2) ──

  // Player joins a type-specific waitlist
  joinTypeWaitlist: (coachId, lessonType, notes = null) => {
    return api.post('/bookings/type-waitlist/join', { coachId, lessonType, notes });
  },

  // Get player's type waitlist entries
  getMyTypeWaitlistEntries: () => {
    return api.get('/bookings/my/type-waitlist');
  },

  // Get coach's type waitlist(s)
  getCoachTypeWaitlists: () => {
    return api.get('/bookings/coach/type-waitlist');
  },

  // Leave a type waitlist
  leaveTypeWaitlist: (waitlistId) => {
    return api.delete(`/bookings/type-waitlist/${waitlistId}`);
  },

  // Accept type waitlist offer
  acceptTypeWaitlistOffer: (waitlistId) => {
    return api.post(`/bookings/type-waitlist/${waitlistId}/accept`, {});
  },

  // Decline type waitlist offer
  declineTypeWaitlistOffer: (waitlistId) => {
    return api.post(`/bookings/type-waitlist/${waitlistId}/decline`, {});
  },

  // ✅ LESSON REMINDERS FOR COACHING BOOKINGS
  // Toggle reminders on/off for a booking
  updateReminderStatus: (bookingId, remindersEnabled) => {
    return api.patch(`/bookings/${bookingId}/reminders/toggle`, { remindersEnabled });
  },

  // Get reminder status for a booking
  getReminderStatus: (bookingId) => {
    return api.get(`/bookings/${bookingId}/reminders`);
  },

  // ── PUBLIC GUEST BOOKING (Non-authenticated users) ──

  // Validate coach exists and has available slots
  validateCoach: (coachId) => {
    return api.get(`/bookings/guest/validate-coach/${coachId}`);
  },

  // Create guest booking (non-authenticated)
  createGuestBooking: (bookingData) => {
    return api.post('/bookings/guest/request', bookingData);
  },

  cancelPendingBooking: (bookingId) => {
    return api.patch(`/bookings/guest/${bookingId}/cancel`, {});
  },

  requestCancellation: (bookingId, reason) => {
    return api.patch(`/bookings/guest/${bookingId}/request-cancel`, { reason });
  },

  approveCancellation: (bookingId) => {
    return api.patch(`/bookings/${bookingId}/approve-cancellation`, {});
  },

  denyCancellation: (bookingId) => {
    return api.patch(`/bookings/${bookingId}/deny-cancellation`, {});
  }
};

// ── PLAYER REQUESTS (New player registration acceptance) ──

export const playerRequests = {
  // Get all pending player requests
  getPendingRequests: () => {
    return api.get('/player-requests/all-pending');
  },

  // Get count of pending requests
  getPendingCount: () => {
    return api.get('/player-requests/pending-count');
  },

  // Accept a player request (add to student list)
  acceptRequest: (requestId) => {
    return api.post(`/player-requests/${requestId}/accept`, {});
  },

  // Disregard a player request
  disregardRequest: (requestId) => {
    return api.post(`/player-requests/${requestId}/disregard`, {});
  },

  // Get history of responded requests
  getHistory: () => {
    return api.get('/player-requests/history');
  }
};

// ── COACH STUDENT SHARING ──

export const studentSharing = {
  // Send share invitation to another coach
  shareStudent: (toCoachEmail, studentId) => {
    return api.post('/coaches/share-student', { toCoachEmail, studentId });
  },

  // Get all shares (sent and received)
  getMyShares: () => {
    return api.get('/coaches/student-shares');
  },

  // Accept a share invitation
  acceptShare: (shareId) => {
    return api.patch(`/coaches/student-shares/${shareId}`, { action: 'accept' });
  },

  // Reject a share invitation
  rejectShare: (shareId) => {
    return api.patch(`/coaches/student-shares/${shareId}`, { action: 'reject' });
  },

  // Revoke sharing access
  revokeShare: (shareId) => {
    return api.delete(`/coaches/student-shares/${shareId}`);
  },

  // Get shared notes for a student
  getSharedNotes: (studentId) => {
    return api.get(`/coaches/shared-student-notes/${studentId}`);
  }
};

// ── COACH PRIORITY SKILLS ──

export const prioritySkills = {
  // Coach assigns priority skills to a player with target measures
  assignPriorities: (playerId, skillsWithMeasures) => {
    return api.post(`/coach/priority-skills/${playerId}`, { skillsWithMeasures });
  },

  // Coach views assigned priorities for a player
  getPlayerPriorities: (playerId) => {
    return api.get(`/coach/priority-skills/${playerId}`);
  },

  // Player views their own priority skills
  getMyPriorities: () => {
    return api.get('/player/priority-skills');
  },

  // Update priorities or deadlines
  updatePriorities: (playerId, data) => {
    return api.patch(`/coach/priority-skills/${playerId}`, data);
  },

  // Clear all priorities for a player
  clearPriorities: (playerId) => {
    return api.delete(`/coach/priority-skills/${playerId}`);
  },

  // Get progress on priority skills
  getProgress: (playerId) => {
    return api.get(`/coach/priority-skills/${playerId}/progress`);
  },

  // Mark a skill as unlocked (called from SkillTree's rating handler, which
  // runs in the COACH's session while editing a student - playerId tells
  // the backend which student's priorities to check, since req.user there
  // is the coach, not the student).
  unlockSkill: (skillId, playerId) => {
    return api.patch(`/priority-skills/${skillId}/unlock`, { playerId });
  }
};

// ── PLAYER PERSONAL NOTES ──

export const personalNotes = {
  // Create a new personal note
  createNote: (title, content, category = null) => {
    return api.post('/personal-notes', { title, content, category });
  },

  // Get all personal notes for the player
  getMyNotes: () => {
    return api.get('/personal-notes');
  },

  // Get all coaches who can be granted access
  getCoaches: () => {
    return api.get('/personal-notes/coaches/list');
  },

  // Get a specific personal note
  getNote: (noteId) => {
    return api.get(`/personal-notes/${noteId}`);
  },

  // Update a personal note
  updateNote: (noteId, title, content, category) => {
    return api.patch(`/personal-notes/${noteId}`, { title, content, category });
  },

  // Delete a personal note
  deleteNote: (noteId) => {
    return api.delete(`/personal-notes/${noteId}`);
  },

  // Grant coach access to a note
  grantAccess: (noteId, coachId, coachName) => {
    return api.patch(`/personal-notes/${noteId}/grant-access`, { coachId, coachName });
  },

  // Revoke coach access to a note
  revokeAccess: (noteId, coachId) => {
    return api.patch(`/personal-notes/${noteId}/revoke-access`, { coachId });
  },

  // Get notes shared with the coach (coach viewing a player's shared notes)
  getSharedNotes: (playerId) => {
    return api.get('/personal-notes/coach/shared-notes', { params: { playerId } });
  },

  // Add a comment to a note (coach only)
  addComment: (playerId, noteId, text) => {
    return api.post(`/personal-notes/${noteId}/comments`, { playerId, text });
  },

  // Delete a comment from a note
  deleteComment: (playerId, noteId, commentId) => {
    return api.delete(`/personal-notes/${noteId}/comments/${commentId}`, {
      data: { playerId }
    });
  },

  // Get the access status of a note
  getAccessStatus: (noteId) => {
    return api.get(`/personal-notes/${noteId}/access-status`);
  },

  // Grant coach access to ALL personal notes
  grantAccessToAllNotes: (coachId, coachName) => {
    return api.patch('/personal-notes/access/grant-all', { coachId, coachName });
  },

  // Revoke coach access from ALL personal notes
  revokeAccessFromAllNotes: (coachId) => {
    return api.patch('/personal-notes/access/revoke-all', { coachId });
  },

  // Get access status for all notes
  getAccessStatusForAllNotes: () => {
    return api.get('/personal-notes/access/all-notes-status');
  }
};

// 🌐 PUBLIC GUEST BOOKING: Coaches API
export const coaches = {
  // Get all coaches for public booking selection
  getAllCoaches: () => {
    return api.get('/coaches/all');
  }
};

// 🎙️ POCKET API: Recording notes and summaries
export const pocket = {
  // Coach-triggered: pull straight from Pocket now instead of waiting for
  // the hourly poll. Can take a few seconds (fetches each new recording's
  // summary from Pocket) so this is a POST the caller should await, not a
  // fire-and-forget.
  forcePoll: () => {
    return api.post('/pocket/force-poll');
  },

  // Get unassigned Pocket notes (recordings without matching students)
  getUnassignedNotes: (coachId = null) => {
    const params = {};
    if (coachId) {
      params.coachId = coachId;
    }
    return api.get('/pocket/unassigned', { params });
  },

  // Assign an unassigned note to one or more students - a group lesson
  // recorded once can become a note for each student in it
  assignNoteToPlayers: (noteId, playerIds) => {
    return api.post(`/pocket/unassigned/${noteId}/assign`, { playerIds });
  },

  // Undo an assignment - removes the materialized note from the player
  // and returns it to the unassigned list
  unassignNote: (playerId, noteId) => {
    return api.post(`/pocket/players/${playerId}/notes/${noteId}/unassign`);
  },

  // Re-fetch a summary straight from Pocket, for when the coach edits it
  // in Pocket after CoachPad already imported it - polling never re-checks
  // an already-imported recording, so this is the only way to pull in
  // a later edit
  refreshUnassignedNote: (noteId) => {
    return api.post(`/pocket/unassigned/${noteId}/refresh`);
  },
  // Give an already-assigned summary to additional students
  assignNoteToMorePlayers: (playerId, noteId, playerIds) => {
    return api.post(`/pocket/players/${playerId}/notes/${noteId}/assign-more`, { playerIds });
  },
  refreshAssignedNote: (playerId, noteId) => {
    return api.post(`/pocket/players/${playerId}/notes/${noteId}/refresh`);
  },

  // Get theme frequency stats - coach-wide, or narrowed to one player's
  // lessons by passing playerId (coachId can be omitted in that case, the
  // backend derives it from the player's own record)
  getThemes: (coachId = null, playerId = null) => {
    const params = {};
    if (coachId) {
      params.coachId = coachId;
    }
    if (playerId) {
      params.playerId = playerId;
    }
    return api.get('/pocket/themes', { params });
  },

  // Get player's Pocket notes
  getPlayerNotes: (playerId, coachId = null) => {
    const params = {};
    if (coachId) {
      params.coachId = coachId;
    }
    return api.get(`/pocket/players/${playerId}/notes`, { params });
  },

  // Every theme the coach has (including ones with zero mentions yet) -
  // for populating a "pick a theme" list, as opposed to getThemes() which
  // only returns themes that have actually come up.
  getAllThemes: (coachId = null) => {
    const params = { includeEmpty: 'true' };
    if (coachId) {
      params.coachId = coachId;
    }
    return api.get('/pocket/themes', { params });
  },

  // Every lesson that mentions a given theme, optionally narrowed to one player
  getThemeLessons: (themeId, playerId = null) => {
    const params = {};
    if (playerId) {
      params.playerId = playerId;
    }
    return api.get(`/pocket/themes/${themeId}/lessons`, { params });
  },

  // Manually label an unassigned note with a theme (existing or new)
  labelUnassignedNoteTheme: (noteId, themeName) => {
    return api.post(`/pocket/unassigned/${noteId}/themes`, { themeName });
  },

  // Manually label an already-assigned (materialized) note with a theme
  labelNoteTheme: (playerId, noteId, themeName) => {
    return api.post(`/notes/players/${playerId}/notes/${noteId}/themes`, { themeName });
  },

  // Stored "clear teachings" pulled from lesson summaries, packed by theme
  getTeachingMoments: (coachId = null, theme = null) => {
    const params = {};
    if (coachId) {
      params.coachId = coachId;
    }
    if (theme) {
      params.theme = theme;
    }
    return api.get('/pocket/teaching-moments', { params });
  },

  // Manually tag a teaching moment with the rhetorical/teaching strategy it uses
  tagTeachingMomentRhetoric: (momentId, tag) => {
    return api.post(`/pocket/teaching-moments/${momentId}/rhetoric`, { tag });
  },

  // Change a teaching moment's category to an existing theme or a new one
  setTeachingMomentTheme: (momentId, themeName) => {
    return api.post(`/pocket/teaching-moments/${momentId}/theme`, { themeName });
  },

  // Star (keep) or hide a teaching: { keep?, hidden? }
  setTeachingMomentState: (momentId, state) => {
    return api.post(`/pocket/teaching-moments/${momentId}/state`, state);
  }
};

// Video plans: short-video ideas built from the coach's teachings (private to the coach)
export const videoPlans = {
  list: () => api.get('/video-plans'),
  create: (title, extra = {}) => api.post('/video-plans', { title, ...extra }),
  get: (id) => api.get(`/video-plans/${id}`),
  update: (id, changes) => api.patch(`/video-plans/${id}`, changes),
  remove: (id) => api.delete(`/video-plans/${id}`),
  addItem: (id, item) => api.post(`/video-plans/${id}/items`, item),
  updateItem: (id, itemId, changes) => api.patch(`/video-plans/${id}/items/${itemId}`, changes),
  removeItem: (id, itemId) => api.delete(`/video-plans/${id}/items/${itemId}`),
  reorder: (id, items) => api.put(`/video-plans/${id}/order`, { items })
};

// Padel Espanol - Spanish learning track (separate from the padel skill tree)
export const spanish = {
  me: () => api.get('/spanish/me'),
  practice: (unit, ids) => api.get('/spanish/practice', { params: { ...(unit ? { unit } : {}), ...(ids && ids.length ? { ids: ids.join(',') } : {}) } }),
  answer: (itemId, correct) => api.post('/spanish/answer', { itemId, correct }),
  today: () => api.get('/spanish/today'),
  startExam: (level) => api.post('/spanish/exam/start', { level }),
  submitExam: (examId, answers) => api.post(`/spanish/exam/${examId}/submit`, { answers }),
  claimToday: (logId) => api.post('/spanish/today/claim', { logId }),

  // coach / headcoach
  students: () => api.get('/spanish/students'),
  student: (playerId) => api.get(`/spanish/students/${playerId}`),
  review: (playerId, reviews, logId) => api.post(`/spanish/students/${playerId}/review`, { reviews, logId }),
  exams: (playerId) => api.get(`/spanish/students/${playerId}/exams`),
  sessions: (playerId) => api.get(`/spanish/students/${playerId}/sessions`),
  planSession: (playerId, { itemIds, bookingId, message }) => api.post(`/spanish/students/${playerId}/session`, { itemIds, bookingId, message }),
  getReview: (logId) => api.get(`/spanish/review/${logId}`),
  items: () => api.get('/spanish/items'),
  uploadAudio: (itemId, blob, filename = 'audio.webm') => {
    const formData = new FormData();
    formData.append('audio', blob, filename);
    return api.post(`/spanish/items/${itemId}/audio`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAudio: (itemId) => api.delete(`/spanish/items/${itemId}/audio`)
};

export default api;